import React, { useState, useEffect, useCallback } from 'react';
import { Mail, CheckCircle, XCircle, Clock, RefreshCw, AlertCircle, MousePointer, Eye, Send } from 'lucide-react';
import { getEmailDeliveryHistory } from '../../../services/emailTrackingService';
import { useAuth } from '../../../contexts/AuthContext';
import Icon from '../../../components/AppIcon';


const STATUS_CONFIG = {
  in_transit: {
    label: 'In Transit',
    icon: Clock,
    color: 'text-amber-600',
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    dot: 'bg-amber-500',
  },
  delivered: {
    label: 'Delivered',
    icon: CheckCircle,
    color: 'text-green-600',
    bg: 'bg-green-50',
    border: 'border-green-200',
    dot: 'bg-green-500',
  },
  bounced: {
    label: 'Bounced',
    icon: XCircle,
    color: 'text-red-600',
    bg: 'bg-red-50',
    border: 'border-red-200',
    dot: 'bg-red-500',
  },
  failed: {
    label: 'Failed',
    icon: AlertCircle,
    color: 'text-red-600',
    bg: 'bg-red-50',
    border: 'border-red-200',
    dot: 'bg-red-500',
  },
  opened: {
    label: 'Opened',
    icon: Eye,
    color: 'text-blue-600',
    bg: 'bg-blue-50',
    border: 'border-blue-200',
    dot: 'bg-blue-500',
  },
  clicked: {
    label: 'Clicked',
    icon: MousePointer,
    color: 'text-purple-600',
    bg: 'bg-purple-50',
    border: 'border-purple-200',
    dot: 'bg-purple-500',
  },
};

const EMAIL_TYPE_LABELS = {
  confirmation: 'Email Confirmation',
  password_reset: 'Password Reset',
};

const formatDate = (dateStr) => {
  if (!dateStr) return null;
  try {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })?.format(new Date(dateStr));
  } catch {
    return dateStr;
  }
};

const StatusBadge = ({ status }) => {
  const config = STATUS_CONFIG?.[status] || STATUS_CONFIG?.in_transit;
  const Icon = config?.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${config?.bg} ${config?.color} ${config?.border}`}>
      <Icon className="w-3 h-3" />
      {config?.label}
    </span>
  );
};

const TimelineStep = ({ label, time, active, done, isLast }) => (
  <div className="flex items-start gap-3">
    <div className="flex flex-col items-center">
      <div className={`w-3 h-3 rounded-full flex-shrink-0 mt-0.5 ${
        done ? 'bg-green-500' : active ? 'bg-amber-500 ring-2 ring-amber-200' : 'bg-gray-200'
      }`} />
      {!isLast && <div className={`w-0.5 h-6 mt-1 ${done ? 'bg-green-200' : 'bg-gray-100'}`} />}
    </div>
    <div className="pb-2">
      <p className={`text-xs font-medium ${done || active ? 'text-foreground' : 'text-muted-foreground'}`}>{label}</p>
      {time && <p className="text-xs text-muted-foreground mt-0.5">{formatDate(time)}</p>}
    </div>
  </div>
);

const EmailTrackingCard = ({ record }) => {
  const [expanded, setExpanded] = useState(false);
  const status = record?.status || 'in_transit';
  const config = STATUS_CONFIG?.[status] || STATUS_CONFIG?.in_transit;

  const timelineSteps = [
    { label: 'Sent', time: record?.sent_at, done: true },
    { label: 'Delivered', time: record?.delivered_at, done: ['delivered', 'opened', 'clicked']?.includes(status) },
    { label: 'Opened', time: record?.opened_at, done: ['opened', 'clicked']?.includes(status) },
    { label: 'Clicked', time: record?.clicked_at, done: status === 'clicked' },
  ];

  const isBounced = ['bounced', 'failed']?.includes(status);

  return (
    <div className="border border-border rounded-xl overflow-hidden">
      <div
        className="flex items-center justify-between p-4 cursor-pointer hover:bg-muted/30 transition-colors"
        onClick={() => setExpanded(p => !p)}
      >
        <div className="flex items-center gap-3 min-w-0">
          <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${config?.bg}`}>
            <Mail className={`w-4 h-4 ${config?.color}`} />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-foreground truncate">
              {EMAIL_TYPE_LABELS?.[record?.email_type] || record?.email_type}
            </p>
            <p className="text-xs text-muted-foreground truncate">{record?.email_address}</p>
          </div>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0 ml-3">
          <StatusBadge status={status} />
          <span className="text-xs text-muted-foreground hidden sm:block">{formatDate(record?.sent_at)}</span>
          <svg
            className={`w-4 h-4 text-muted-foreground transition-transform ${expanded ? 'rotate-180' : ''}`}
            fill="none" viewBox="0 0 24 24" stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>

      {expanded && (
        <div className="border-t border-border p-4 bg-muted/20">
          {isBounced ? (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-red-700">Delivery Failed</p>
                  <p className="text-xs text-red-600 mt-0.5">{record?.bounce_reason || 'The email could not be delivered to this address.'}</p>
                </div>
              </div>
            </div>
          ) : (
            <div className="mb-4">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">Delivery Timeline</p>
              <div>
                {timelineSteps?.map((step, idx) => (
                  <TimelineStep
                    key={step?.label}
                    label={step?.label}
                    time={step?.time}
                    done={step?.done}
                    active={!step?.done && timelineSteps?.[idx - 1]?.done}
                    isLast={idx === timelineSteps?.length - 1}
                  />
                ))}
              </div>
            </div>
          )}

          {record?.resend_email_id && (
            <div className="pt-3 border-t border-border">
              <p className="text-xs text-muted-foreground">
                <span className="font-medium">Email ID:</span> <span className="font-mono">{record?.resend_email_id}</span>
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const EmailDeliveryTracker = () => {
  const { user } = useAuth();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [filter, setFilter] = useState('all');

  const fetchRecords = useCallback(async (isRefresh = false) => {
    if (!user?.id) return;
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    setError(null);
    try {
      const result = await getEmailDeliveryHistory(user?.id);
      if (result?.success) {
        setRecords(result?.data || []);
      } else {
        setError(result?.error || 'Failed to load email tracking data');
      }
    } catch (err) {
      setError(err?.message || 'Unexpected error');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [user?.id]);

  useEffect(() => {
    fetchRecords();
  }, [fetchRecords]);

  const filteredRecords = filter === 'all'
    ? records
    : records?.filter(r => {
        if (filter === 'confirmation') return r?.email_type === 'confirmation';
        if (filter === 'password_reset') return r?.email_type === 'password_reset';
        if (filter === 'issues') return ['bounced', 'failed']?.includes(r?.status);
        return true;
      });

  const stats = {
    total: records?.length || 0,
    delivered: records?.filter(r => ['delivered', 'opened', 'clicked']?.includes(r?.status))?.length || 0,
    inTransit: records?.filter(r => r?.status === 'in_transit')?.length || 0,
    issues: records?.filter(r => ['bounced', 'failed']?.includes(r?.status))?.length || 0,
  };

  if (loading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3]?.map(i => (
          <div key={i} className="h-16 bg-muted/40 rounded-xl animate-pulse" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-foreground">Email Delivery Tracking</h2>
          <p className="text-sm text-muted-foreground mt-0.5">Monitor delivery status of confirmation and password-reset emails</p>
        </div>
        <button
          onClick={() => fetchRecords(true)}
          disabled={refreshing}
          className="flex items-center gap-1.5 px-3 py-2 text-sm text-muted-foreground hover:text-foreground border border-border rounded-lg hover:bg-muted/50 transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
          <span>Refresh</span>
        </button>
      </div>
      {/* Stats Row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Total Sent', value: stats?.total, icon: Send, color: 'text-foreground', bg: 'bg-muted/40' },
          { label: 'Delivered', value: stats?.delivered, icon: CheckCircle, color: 'text-green-600', bg: 'bg-green-50' },
          { label: 'In Transit', value: stats?.inTransit, icon: Clock, color: 'text-amber-600', bg: 'bg-amber-50' },
          { label: 'Issues', value: stats?.issues, icon: AlertCircle, color: 'text-red-600', bg: 'bg-red-50' },
        ]?.map(stat => {
          const Icon = stat?.icon;
          return (
            <div key={stat?.label} className={`${stat?.bg} border border-border rounded-xl p-3`}>
              <div className="flex items-center gap-2 mb-1">
                <Icon className={`w-4 h-4 ${stat?.color}`} />
                <span className="text-xs text-muted-foreground font-medium">{stat?.label}</span>
              </div>
              <p className={`text-2xl font-bold ${stat?.color}`}>{stat?.value}</p>
            </div>
          );
        })}
      </div>
      {/* Filter Tabs */}
      <div className="flex gap-1 p-1 bg-muted/40 rounded-xl w-fit">
        {[
          { id: 'all', label: 'All' },
          { id: 'confirmation', label: 'Confirmation' },
          { id: 'password_reset', label: 'Password Reset' },
          { id: 'issues', label: 'Issues' },
        ]?.map(tab => (
          <button
            key={tab?.id}
            onClick={() => setFilter(tab?.id)}
            className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
              filter === tab?.id
                ? 'bg-card text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {tab?.label}
          </button>
        ))}
      </div>
      {/* Error State */}
      {error && (
        <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-xl">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-red-700">Failed to load tracking data</p>
            <p className="text-xs text-red-600 mt-0.5">{error}</p>
          </div>
        </div>
      )}
      {/* Records List */}
      {!error && filteredRecords?.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <div className="w-14 h-14 bg-muted/40 rounded-full flex items-center justify-center mb-4">
            <Mail className="w-6 h-6 text-muted-foreground" />
          </div>
          <p className="font-medium text-foreground">No emails tracked yet</p>
          <p className="text-sm text-muted-foreground mt-1 max-w-xs">
            {filter === 'all' ?'Email delivery events will appear here once you send confirmation or password-reset emails.'
              : `No ${filter === 'issues' ? 'issue' : filter?.replace('_', ' ')} emails found.`}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredRecords?.map(record => (
            <EmailTrackingCard key={record?.id} record={record} />
          ))}
        </div>
      )}
      {/* Webhook Info Banner */}
      <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl">
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
            <Mail className="w-4 h-4 text-blue-600" />
          </div>
          <div>
            <p className="text-sm font-semibold text-blue-800">Real-time Delivery Updates</p>
            <p className="text-xs text-blue-700 mt-0.5 leading-relaxed">
              Status updates are powered by Resend webhooks. Configure your webhook endpoint
              <span className="font-mono font-medium"> /functions/v1/resend-webhook </span>
              in your Resend dashboard to receive live delivery events.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailDeliveryTracker;
